import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { localDayKey, type AbsenceSeriesDTO, type AbsenceTypeDTO } from '@wf/shared'
import { api, ApiError } from '../api/client'
import { newUuid } from '../offline/uuid'
import { formatDateDE } from '../time/format'
import { useAbsenceSeries, useCancelAbsenceSeries, useCreateAbsenceSeries } from './useZeiten'

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

function serienLabel(s: AbsenceSeriesDTO): string {
  const tage = s.weekdays.map((w) => WOCHENTAGE[w] ?? '?').join(', ')
  return `${tage}${s.halfDay ? ' (halber Tag)' : ''}`
}

/**
 * Wiederkehrende Abwesenheit als EIN Vorgang (Azubi-Block) — der Regelfall ist der Berufsschultag.
 * Ohne diese Karte müsste der Chef ~40 Einzeleinträge pro Ausbildungsjahr anlegen; ein vergessener
 * Schultag zementiert sonst ein Minus über den Monatsabschluss hinweg.
 *
 * Der Server ist autoritativ: er prüft das Ausbildungsverhältnis, deckelt Wochentage/Zeitraum/Zeilenzahl
 * und meldet je Tag zurück, ob er übersprungen wurde (Überschneidung) oder wirkungslos bleibt (Feiertag /
 * kein Solltag). Dieser ehrliche Teilerfolg wird hier angezeigt, statt „gespeichert" zu behaupten.
 */
export function AbsenceSeriesCard({ types }: { types: AbsenceTypeDTO[] }) {
  const users = useQuery({ queryKey: ['zeiten', 'absence-users'], queryFn: api.listUsers })
  const serien = useAbsenceSeries()
  const create = useCreateAbsenceSeries()
  const cancel = useCancelAbsenceSeries()

  const [userId, setUserId] = useState('')
  const [typeId, setTypeId] = useState('')
  const [weekdays, setWeekdays] = useState<number[]>([])
  const [von, setVon] = useState('')
  const [bis, setBis] = useState('')
  const [halbtags, setHalbtags] = useState(false)
  const [ferienVon, setFerienVon] = useState('')
  const [ferienBis, setFerienBis] = useState('')
  // Eigene id je Eintrag: zwei identische Zeiträume kollidierten sonst im React-Key, und das Entfernen
  // per Index traf die falsche Zeile.
  const [ferien, setFerien] = useState<Array<{ id: string; from: string; to: string }>>([])
  const [uuid, setUuid] = useState(newUuid)
  const [beendenId, setBeendenId] = useState<number | null>(null)
  // Der Server markiert eine beendete Serie NICHT als beendet (er storniert nur ihre Zeilen) — die
  // Protokoll-Zeile bleibt in der Liste stehen. Bis das serverseitig gelöst ist, merken wir uns die
  // beendeten Serien für diese Sitzung, damit der Chef nicht auf einen funktionslosen Knopf tippt.
  const [beendet, setBeendet] = useState<number[]>([])

  const teamUsers = (users.data?.users ?? []).filter((u) => u.active === 1)
  const nameOf = useMemo(() => new Map(teamUsers.map((u) => [u.id, u.name])), [teamUsers])
  // Berufsschule/Fortbildung zuerst — das ist der Anwendungsfall dieser Karte.
  const typeOptions = useMemo(() => [...types].sort((a, b) => (a.key === 'berufsschule' ? -1 : b.key === 'berufsschule' ? 1 : 0)), [types])

  const [ferienFehler, setFerienFehler] = useState<string | null>(null)
  const toggleTag = (w: number) => setWeekdays((prev) => (prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w].sort()))
  const ferienHinzu = () => {
    // Früher ein stiller `return` — der Chef tippte einen vertauschten Zeitraum, klickte, und nichts geschah.
    if (!ferienVon || !ferienBis) return setFerienFehler('Bitte beide Datumsfelder ausfüllen.')
    if (ferienBis < ferienVon) return setFerienFehler('Das Bis-Datum liegt vor dem Von-Datum.')
    setFerien((prev) => [...prev, { id: newUuid(), from: ferienVon, to: ferienBis }])
    setFerienVon('')
    setFerienBis('')
    setFerienFehler(null)
  }

  const kannSpeichern = userId !== '' && typeId !== '' && weekdays.length > 0 && von !== '' && bis !== '' && bis >= von && !create.isPending
  // Ein ausgefüllter, aber nicht per „Hinzufügen" übernommener Zeitraum wäre beim Speichern still weg —
  // und damit stünde der Azubi an seinen Ferientagen als anwesend erwartet da.
  const offenerFerienEintrag = ferienVon !== '' || ferienBis !== ''

  const speichern = () => {
    if (!kannSpeichern) return
    if (offenerFerienEintrag) return setFerienFehler('Der eingetragene Ausnahme-Zeitraum ist noch nicht übernommen — bitte „Hinzufügen" oder die Felder leeren.')
    create.mutate(
      {
        type_id: Number(typeId),
        user_id: Number(userId),
        weekdays,
        from_day: von,
        to_day: bis,
        half_day: halbtags,
        exceptions: ferien.map(({ from, to }) => ({ from, to })),
        client_uuid: uuid,
      },
      {
        // ⚠️ Der Schlüssel wird NUR nach Erfolg oder nach einer DEFINITIVEN Ablehnung erneuert.
        // Ein zwischenzeitlicher „Fix" rotierte ihn per onSettled nach jedem Versuch — und brach damit
        // genau die Idempotenz, für die er da ist: bricht das Netz NACH dem Schreiben ab, sieht der Chef
        // einen Fehler, sendet erneut, und mit frischem Schlüssel greift der Reparatur-Lookup des Servers
        // nicht mehr → eine zweite Serie entsteht, die erste bleibt halb geschrieben zurück.
        // Bei 4xx dagegen hat der Server VOR jedem Schreibvorgang abgelehnt (alle Guards liegen davor),
        // der Schlüssel ist also unverbraucht und darf erneuert werden.
        onError: (e) => {
          if (e instanceof ApiError && e.status >= 400 && e.status < 500) setUuid(newUuid())
        },
        onSuccess: () => {
          setUuid(newUuid())
          setWeekdays([])
          setVon('')
          setBis('')
          setFerien([])
          setHalbtags(false)
          setFerienFehler(null)
        },
      },
    )
  }

  const fehler = create.error instanceof ApiError ? create.error.message : create.isError ? 'Serie konnte nicht angelegt werden.' : null
  const ergebnis = create.data

  return (
    <section className="wf-card" aria-label="Wiederkehrende Abwesenheit">
      <h3 className="wf-card__title">Wiederkehrend eintragen (Berufsschule)</h3>
      <p className="wf-hint">
        Legt für jeden Termin einen eigenen, sofort genehmigten Eintrag an — so bleibt jeder Tag einzeln korrigierbar und der
        Monatsabschluss wird nicht blockiert.
      </p>

      <div className="wf-row">
        <label className="wf-field">
          <span className="wf-label">Mitarbeiter</span>
          <select className="wf-input" aria-label="Mitarbeiter" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={create.isPending}>
            <option value="">bitte wählen</option>
            {teamUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">Art</span>
          <select className="wf-input" aria-label="Art" value={typeId} onChange={(e) => setTypeId(e.target.value)} disabled={create.isPending}>
            <option value="">bitte wählen</option>
            {typeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="wf-field">
        <legend className="wf-label">Wochentage (höchstens zwei)</legend>
        <div className="wf-row">
          {WOCHENTAGE.map((label, w) => (
            <label key={label} className="wf-check">
              <input
                type="checkbox"
                checked={weekdays.includes(w)}
                onChange={() => toggleTag(w)}
                disabled={create.isPending || (!weekdays.includes(w) && weekdays.length >= 2)}
                aria-label={label}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="wf-row">
        <label className="wf-field">
          <span className="wf-label">von</span>
          <input className="wf-input" type="date" aria-label="Serie von" value={von} onChange={(e) => setVon(e.target.value)} disabled={create.isPending} />
        </label>
        <label className="wf-field">
          <span className="wf-label">bis</span>
          <input className="wf-input" type="date" aria-label="Serie bis" value={bis} onChange={(e) => setBis(e.target.value)} disabled={create.isPending} />
        </label>
        <label className="wf-check">
          <input type="checkbox" checked={halbtags} onChange={(e) => setHalbtags(e.target.checked)} disabled={create.isPending} aria-label="Halber Tag" />
          <span>halber Tag</span>
        </label>
      </div>

      <fieldset className="wf-field">
        <legend className="wf-label">Ferien / Ausnahmen</legend>
        <div className="wf-row">
          <input className="wf-input" type="date" aria-label="Ausnahme von" value={ferienVon} onChange={(e) => setFerienVon(e.target.value)} disabled={create.isPending} />
          <input className="wf-input" type="date" aria-label="Ausnahme bis" value={ferienBis} onChange={(e) => setFerienBis(e.target.value)} disabled={create.isPending} />
          <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={ferienHinzu} disabled={create.isPending || !ferienVon || !ferienBis}>
            Hinzufügen
          </button>
        </div>
        {ferienFehler ? (
          <p className="wf-absform__err" role="alert">
            {ferienFehler}
          </p>
        ) : null}
        {ferien.length > 0 ? (
          <ul className="wf-list">
            {ferien.map((f) => (
              <li key={f.id}>
                {formatDateDE(f.from)} – {formatDateDE(f.to)}{' '}
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setFerien((prev) => prev.filter((x) => x.id !== f.id))}>
                  entfernen
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="sub">In diesen Zeiträumen entfällt der Termin (z. B. Schulferien).</p>
        )}
      </fieldset>

      {fehler ? (
        <p className="wf-absform__err" role="alert">
          {fehler}
        </p>
      ) : null}
      {ergebnis ? (
        <div className="wf-note" role="status">
          {/* Der Replay kommt aus dem SERVER-Flag, nicht aus created===0: letzteres bedeutet auch „alle Tage
              kollidierten mit bestehenden Einträgen" — dort wäre „bereits angelegt" schlicht falsch. */}
          <b>
            {ergebnis.idempotent
              ? 'Diese Serie steht bereits — es wurde nichts doppelt angelegt.'
              : ergebnis.created === 0
                ? 'Kein Termin angelegt — siehe unten.'
                : `${ergebnis.created} Termine angelegt.`}
          </b>
          {ergebnis.skipped.length > 0 ? (
            <ul className="wf-list">
              {ergebnis.skipped.map((s) => (
                <li key={`${s.day}-${s.reason}`}>
                  {formatDateDE(s.day)} —{' '}
                  {s.reason === 'overlap' ? 'übersprungen: es gibt dort schon einen Eintrag derselben Art' : 'kein Solltag (Feiertag oder frei) — ohne Gutschrift'}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="wf-form__actions">
        <button type="button" className="wf-btn" onClick={speichern} disabled={!kannSpeichern}>
          {create.isPending ? 'Wird angelegt…' : 'Serie anlegen'}
        </button>
      </div>

      <h4 className="wf-card__subtitle">Laufende Serien</h4>
      {serien.isError ? (
        <p className="wf-absform__err" role="alert">
          Serien konnten nicht geladen werden.
        </p>
      ) : null}
      {serien.isSuccess && (serien.data?.series.length ?? 0) === 0 ? <p className="sub">Noch keine wiederkehrenden Einträge.</p> : null}
      {(serien.data?.series ?? []).map((s) => {
        // Eine Serie, deren Zeitraum vorbei ist, kann nichts mehr stornieren — der Knopf wäre eine
        // funktionslose Schaltfläche (der Server meldete 0 storniert).
        const abgelaufen = s.toDay < localDayKey(Date.now()) || beendet.includes(s.id)
        return (
        <div key={s.id} className="wf-absreq">
          <span>
            <b>{nameOf.get(s.userId) ?? 'Mitarbeiter'}</b>: {s.label} · {serienLabel(s)} · {formatDateDE(s.fromDay)} – {formatDateDE(s.toDay)}
            {s.skippedCount > 0 ? <span className="sub"> ({s.skippedCount} übersprungen)</span> : null}
            {beendet.includes(s.id) ? <span className="wf-tag">beendet</span> : abgelaufen ? <span className="wf-tag">abgelaufen</span> : null}
          </span>
          {/* Zwei-Schritt statt Rückfrage-Dialog (Muster des Verfall-Assistenten): ein einzelner Tap
              storniert bis zu 100 Termine — auf einem Werkstatt-Tablet zu leicht ausgelöst. */}
          {abgelaufen ? null : beendenId === s.id ? (
            <>
              <button
                type="button"
                className="wf-btn wf-btn--sm"
                onClick={() => {
                  setBeendenId(null)
                  cancel.mutate({ id: s.id, body: { from_day: localDayKey(Date.now()) } }, { onSuccess: () => setBeendet((prev) => [...prev, s.id]) })
                }}
                disabled={cancel.isPending}
              >
                Wirklich beenden
              </button>
              <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setBeendenId(null)}>
                Abbrechen
              </button>
            </>
          ) : (
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              onClick={() => setBeendenId(s.id)}
              disabled={cancel.isPending}
              title="Beendet die Serie ab heute — vergangene Termine bleiben erhalten"
            >
              Ab heute beenden
            </button>
          )}
        </div>
        )
      })}
      {cancel.isError ? (
        <p className="wf-absform__err" role="alert">
          {cancel.error instanceof ApiError ? cancel.error.message : 'Serie konnte nicht beendet werden.'}
        </p>
      ) : null}
      {cancel.isSuccess ? (
        <p className="sub" role="status">
          {cancel.data.cancelled} Termine storniert{cancel.data.frozen > 0 ? `, ${cancel.data.frozen} in abgeschlossenen Monaten belassen` : ''}.
        </p>
      ) : null}
    </section>
  )
}
